<!-- Sign up -->
<script setup lang="ts">
import type { Ref } from "vue";
import { ref, onMounted } from "vue";
import { signUp, signIn, confirmSignUp, getCurrentUser, resendSignUpCode } from "aws-amplify/auth";
import { requiredRule } from "@/utilities/requiredRule";
import PasswordRequirements from "@/components/PasswordRequirements.vue";
import _isEmpty from "lodash/isEmpty";
import _get from "lodash/get";
import { useMutation } from "@vue/apollo-composable";
import gql from "graphql-tag";
// import { getFriendlyStringForErrorTypeName } from '@/functions/getFriendlyStringForErrorTypeName'
import { useRouter } from "vue-router";
import { createCustomer } from "@/graphql/functions/createCustomer";

const router = useRouter();
const has_check_user_authed_run: Ref<boolean> = ref(false);
const password_requirements_password_valid: Ref<boolean> = ref(false);
const sign_up_step = ref({
    form_valid: false,
    firstname: null,
    lastname: null,
    company_name: null,
    email: null,
    password: null,
    confirm_password: null,
    make_password_visible: false,
    error_message: null,
});
const sign_up_confirm_step = ref({
    error_message: null,
});
const loading: Ref<boolean> = ref(false);
const signup_confirmation_code: Ref<string> = ref("");
const show_signup_confirmation: Ref<boolean> = ref(false);
const confirmation_code_delivery_destination: Ref<string> = ref("");
const show_signup_success_screen: Ref<boolean> = ref(false);

function onSubmit() {
    sign_up_step.value.error_message = "";

    if (!sign_up_step.value.form_valid) return;

    loading.value = true;

    signUp({ username: sign_up_step.value.email, password: sign_up_step.value.password })
        .then((response) => {
            if (response.isSignUpComplete === false) {
                if (response.nextStep.signUpStep === "CONFIRM_SIGN_UP") {
                    confirmation_code_delivery_destination.value = response.nextStep.codeDeliveryDetails.destination;
                    show_signup_confirmation.value = true;
                }
            }

            loading.value = false;
        })
        .catch((error) => {
            if (_get(error, "name", "") === "UsernameExistsException") {
                resendSignUpCode({ username: sign_up_step.value.email })
                    .then((response) => {
                        show_signup_confirmation.value = true;
                        loading.value = false;
                    })
                    .catch((error) => {
                        console.error(error);
                    });
            } else {
                console.error(error);
                sign_up_step.value.error_message = error.message;
                loading.value = false;
            }
        });
}

// Calls the signup confirmation function from Amplify, logs in, then calls the public signup function in GQL.
function confirmUserSignUp() {
    return new Promise(async (resolve, reject) => {
        try {
            loading.value = true;

            const response = await confirmSignUp({ confirmationCode: signup_confirmation_code.value, username: sign_up_step.value.email });

            if (response.isSignUpComplete === true) {
                // If Cognito sign up is successful, log the user in before calling the public signup function in GQL.
                const login_response = await signIn({ username: sign_up_step.value.email, password: sign_up_step.value.password });

                if (login_response.isSignedIn === false) {
                    if (login_response.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
                        // this.current_step = 'new_password_required'
                    }
                } else {
                    // Now that the user is logged in, call the public signup function in GQL.
                    await runPublicSignup();

                    show_signup_success_screen.value = true;
                    loading.value = false;
                    resolve(true);
                    return;
                }
            }
        } catch (error) {
            console.error("Error in confirmUserSignUp():", error);
            const error_msg = _get(error, "message", "Unknown error.");
            sign_up_confirm_step.value.error_message = `Signup failed. ${error_msg}`;
            loading.value = false;
            reject();
        }
    });
}

// Calls the public signup function in GQL to create a company for the user.
function runPublicSignup() {
    return new Promise(async (resolve, reject) => {
        try {
            await createCustomer(sign_up_step.value.firstname, sign_up_step.value.lastname);

            resolve(true);
            return;
        } catch (error) {
            console.error(error);
            const error_message = _get(error, "networkError.result.errors[0].message", "Unknown error.");
            sign_up_confirm_step.value.error_message = error_message;
            reject();
        }
    });
}

function onSuccessfulContinueButton() {
    return new Promise(async (resolve, reject) => {
        try {
            loading.value = true;

            // Call the function to populate the user's meta data in the store, which is needed before the user navigates to the homepage.
            // await userStore.fetchCurrentUsersMeta()
            router.push({ name: "login" });

            loading.value = false;

            resolve(true);
            return;
        } catch (error) {
            console.error(error);
            loading.value = false;
            reject();
        }
    });
}

onMounted(() => {
    if (has_check_user_authed_run.value === false) {
        has_check_user_authed_run.value = true;

        // On page load, fetch the auth session to check if the user is already logged in. If they are, redirect away from the sign up page.
        getCurrentUser()
            .then((auth) => {
                console.log("User logged in - redirecting...");
                router.push({ path: "/" });
            })
            .catch((error) => {
                // Nothing to do if the user is not logged in.
            });
    }
});
</script>

<template>
    <div class="signup_container">
        <v-card class="mx-auto px-xs-1 px-6 py-8" max-width="544" elevation="0">
            <template v-if="show_signup_success_screen">
                <v-empty-state style="padding: 0">
                    <template v-slot:media>
                        <v-icon color="success" size="52">mdi-check-circle</v-icon>
                    </template>

                    <template v-slot:headline>
                        <div class="text-h4">Success</div>
                    </template>

                    <template v-slot:text>
                        <div>Thank you for creating an account!</div>

                        <v-btn color="var(--vt-c-orange)" class="text-white" @click="onSuccessfulContinueButton" style="margin-top: 40px" :loading="loading">Continue</v-btn>
                    </template>
                </v-empty-state>
            </template>

            <template v-else-if="show_signup_confirmation">
                <p class="confirmation_code_desc">A Confirmation code has been sent to {{ confirmation_code_delivery_destination === "" ? " the email" : confirmation_code_delivery_destination }}.<br />Enter your confirmation code below to confirm your account.</p>

                <v-text-field v-model="signup_confirmation_code" :readonly="loading" :rules="[requiredRule]" label="Confirmation code" placeholder="Enter your code" variant="outlined" class="mb-2"></v-text-field>

                <v-btn :disabled="_isEmpty(signup_confirmation_code)" :loading="loading" color="var(--vt-c-orange)" size="large" type="submit" variant="elevated" block class="text-white" @click="confirmUserSignUp"> Confirm </v-btn>

                <v-alert type="error" v-if="!_isEmpty(sign_up_confirm_step.error_message)" class="mt-6" icon="mdi-alert-circle">
                    <span>{{ sign_up_confirm_step.error_message }}</span>
                </v-alert>
            </template>

            <v-form class="signup-form" v-model="sign_up_step.form_valid" @submit.prevent="onSubmit" v-else>
                <h1>Register with MyTrainTicket</h1>

                <v-text-field v-model="sign_up_step.email" :readonly="loading" :rules="[requiredRule]" class="mb-2" label="Email Address" variant="outlined" autocomplete="email"></v-text-field>

                <v-text-field v-model="sign_up_step.firstname" :readonly="loading" :rules="[requiredRule]" class="mb-2" label="Firstname" variant="outlined" autocomplete="name"></v-text-field>

                <v-text-field v-model="sign_up_step.lastname" :readonly="loading" :rules="[requiredRule]" class="mb-2" label="Lastname" variant="outlined" autocomplete="name"></v-text-field>

                <v-row class="password-row">
                    <v-col cols="12" sm="6">
                        <v-text-field v-model="sign_up_step.password" :readonly="loading" :rules="[requiredRule]" label="Password" :type="sign_up_step.make_password_visible ? 'text' : 'password'" @click:append-inner="sign_up_step.make_password_visible = !sign_up_step.make_password_visible" :append-inner-icon="sign_up_step.make_password_visible ? 'mdi-eye' : 'mdi-eye-off'" variant="outlined"></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6">
                        <v-text-field v-model="sign_up_step.confirm_password" :readonly="loading" :rules="[requiredRule]" label="Confirm Password" :type="sign_up_step.make_password_visible ? 'text' : 'password'" @click:append-inner="sign_up_step.make_password_visible = !sign_up_step.make_password_visible" :append-inner-icon="sign_up_step.make_password_visible ? 'mdi-eye' : 'mdi-eye-off'" variant="outlined"></v-text-field>
                    </v-col>
                </v-row>

                <PasswordRequirements :password="sign_up_step.password" @passwordValid="(val) => (password_requirements_password_valid = val)" class="my-1"></PasswordRequirements>
                <br />

                <v-alert type="error" v-if="!_isEmpty(sign_up_step.error_message)" class="mb-4" icon="mdi-alert-circle">
                    <span>{{ sign_up_step.error_message }}</span>
                </v-alert>

                <v-btn :disabled="!sign_up_step.form_valid || !password_requirements_password_valid" :loading="loading" color="var(--vt-c-orange)" size="large" type="submit" variant="elevated" block class="text-white"> Register </v-btn>
            </v-form>
        </v-card>
    </div>
</template>

<style scoped>
.signup_container > .v-card {
    min-width: 344px;
}
.confirmation_code_desc {
    margin: -22px 0 20px 0;
    text-align: center;
}

.signup-form h1 {
    margin: -32px 0 22px 0;
    text-align: center;
    font-weight: 400;
}

.signup-form p {
    margin-bottom: 20px;
}

/* Override global style */
:deep(input[type="text"].v-field__input) {
    padding-inline: var(--v-field-padding-start) var(--v-field-padding-end);
    padding-top: var(--v-field-input-padding-top);
    padding-bottom: var(--v-field-input-padding-bottom);
}
@media (max-width: 600px) {
    .signup_container > .v-card {
        min-width: 95vw;
    }
    :deep(.password-row > .v-col-12) {
        padding-top: 12px;
        padding-bottom: 0;
    }
    :deep(.password-row > .v-col-12:last-child) {
        padding-top: 8px;
        padding-bottom: 10px;
    }
    .signup-form h1 {
        font-size: 1.3rem;
    }
}
</style>
